import { Divider, makeStyles, mergeClasses, shorthands, Text, tokens } from '@fluentui/react-components'
import Card from '../../../components/Card'
import * as React from 'react'
import { ReactElement, useContext } from 'react'
import MoneyText from '../../Text/Currency/MoneyText'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import { LegalAidCertificateDto } from './LegalAidCertificateForm'
import useCurrency from '../../../hooks/useCurrency'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import { UnitDescription } from './UnitDescription'

export type CalculationProps = {
    subtotal: number // The full compensation before tax.
    vatPercentage: number // The percentage VAT that is added.
    vat: number // The amount of VAT tax.
    total: number // The full compensation after tax.
    compensation: number // The payable compensation by the legal aid board after deduction of the own contribution.
}

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gridTemplateColumns: '1fr 80px',
        ...shorthands.gap(tokens.spacingHorizontalL, tokens.spacingVerticalS),
        alignItems: 'center',
    },
    description: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        alignItems: 'baseline',
        ...shorthands.gap(tokens.spacingHorizontalL, tokens.spacingVerticalS),
    },
    divider: {
        gridColumnEnd: 'span 2',
    },
    row: {
        display: 'contents',
    },
})

export default function Calculation(props: CalculationProps): ReactElement {
    const {
        settings: { legalAidCertificate: settings },
    } = useContext<GlobalContextType>(GlobalContext)
    const { values } = useFormikContext<LegalAidCertificateDto>()
    const { isViewport } = useViewport()
    const styles = useStyles()
    const { currency } = useCurrency(values.currency)
    const description = mergeClasses(isViewport('=', Viewport.Large) && styles.description)

    return (
        <Card>
            <Text size={500}>Berekening vergoeding</Text>
            <div className={mergeClasses(styles.container)}>
                <div className={mergeClasses(styles.row)}>
                    <div className={description}>
                        <Text block>Basisvergoeding</Text>
                        <UnitDescription
                            unit="punten"
                            unitFormat="0.00"
                            unitAmount={values.baseHours}
                            defaultUnitRate={settings.baseFeeHour}
                            rate={values.baseFee}
                            currency={currency}
                        />
                    </div>
                    <MoneyText amount={values.baseFee} currency={currency} align="right" />
                </div>
                <div className={mergeClasses(styles.row)}>
                    <div className={description}>
                        <Text block>Reistijdvergoeding</Text>
                        <UnitDescription
                            unit="punten"
                            unitFormat="0.00"
                            unitAmount={values.travelHours}
                            defaultUnitRate={settings.travelFeeHour}
                            rate={values.travelFee}
                            currency={currency}
                        />
                    </div>
                    <MoneyText amount={values.travelFee} currency={currency} align="right" />
                </div>
                <div className={mergeClasses(styles.row)}>
                    <div className={description}>
                        <Text block>Reiskostenvergoeding</Text>
                        <UnitDescription
                            unit="km"
                            unitFormat="0"
                            unitAmount={values.travelDistance}
                            defaultUnitRate={settings.travelFeeHour}
                            rate={values.travelExpenses}
                            currency={currency}
                        />
                    </div>
                    <MoneyText amount={values.travelExpenses} currency={currency} align="right" />
                </div>
                <div className={mergeClasses(styles.row)}>
                    <Text>Vaste kostenvergoeding</Text>
                    <MoneyText amount={values.fixedCompensation} currency={currency} align="right" />
                </div>
                <div className={mergeClasses(styles.row)}>
                    <div className={description}>
                        <Text>BTW</Text>
                        <Text block size={200}>
                            {props.vatPercentage.toString()}% over&nbsp;
                            <MoneyText amount={props.subtotal} currency={currency} textProps={{ size: 200 }} inline />
                        </Text>
                    </div>
                    <MoneyText amount={props.vat} currency={currency} align="right" />
                </div>
                <Divider className={mergeClasses(styles.divider)} />
                <div className={mergeClasses(styles.row)}>
                    <Text weight="medium">Totaal vergoeding</Text>
                    <MoneyText
                        amount={props.total}
                        currency={currency}
                        align="right"
                        textProps={{ weight: 'medium' }}
                    />
                </div>
                <div className={mergeClasses(styles.row)}>
                    <Text>Af: eigen bijdrage</Text>
                    <MoneyText amount={values.contribution} currency={currency} align="right" />
                </div>
                <Divider className={mergeClasses(styles.divider)} />
                <div className={mergeClasses(styles.row)}>
                    <Text weight="medium">Uit te betalen</Text>
                    <MoneyText
                        amount={props.compensation}
                        currency={currency}
                        align="right"
                        textProps={{ weight: 'medium' }}
                    />
                </div>
            </div>
        </Card>
    )
}
